import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { UserProvider, useUser } from './context/UserContext';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Companies from './pages/Companies';
import CompanyDetail from './pages/CompanyDetail';
import TypsaOrgUnitDetail from './pages/TypsaOrgUnitDetail';
import Contacts from './pages/Contacts';
import ContactDetail from './pages/ContactDetail';
import Tasks from './pages/Tasks';
import Activities from './pages/Activities';
import Pipeline from './pages/Pipeline';
import OpportunityDetail from './pages/OpportunityDetail';
import TextNoteDetail from './pages/TextNoteDetail';
import VoiceNoteCreate from './pages/VoiceNoteCreate';
import VoiceNoteDetail from './pages/VoiceNoteDetail';
import MySpace from './pages/MySpace';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TechnicalReport from './pages/TechnicalReport';
import UsersAdmin from './pages/UsersAdmin';
import '@/App.css';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, needsPasswordChange } = useUser();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Force password change if needed (except if already on change-password page)
  if (needsPasswordChange && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return children;
};

// Public Route wrapper (for login page - redirect if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, needsPasswordChange } = useUser();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    if (needsPasswordChange) {
      return <Navigate to="/change-password" replace />;
    }
    // Redirect to where they came from, or home
    const from = location.state?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  return children;
};

// Change Password Route (only accessible if authenticated but needs password change)
const ChangePasswordRoute = ({ children }) => {
  const { isAuthenticated, loading } = useUser();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      
      {/* Password recovery routes (public) */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Change password route (authenticated but force change) */}
      <Route path="/change-password" element={
        <ChangePasswordRoute>
          <ChangePassword />
        </ChangePasswordRoute>
      } />

      {/* Protected routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="companies" element={<Companies />} />
        <Route path="companies/:id" element={<CompanyDetail />} />
        <Route path="typsa-org-units/:id" element={<TypsaOrgUnitDetail />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="contacts/:id" element={<ContactDetail />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="activities" element={<Activities />} />
        <Route path="pipeline" element={<Pipeline />} />
        <Route path="pipeline/:id" element={<OpportunityDetail />} />
        <Route path="text-notes" element={<Navigate to="/my-space" replace />} />
        <Route path="text-notes/:id" element={<TextNoteDetail />} />
        <Route path="voice-notes" element={<Navigate to="/my-space" replace />} />
        <Route path="voice-notes/create" element={<VoiceNoteCreate />} />
        <Route path="voice-notes/:id" element={<VoiceNoteDetail />} />
        <Route path="my-space" element={<MySpace />} />
        <Route path="technical-report" element={<TechnicalReport />} />
        <Route path="admin/users" element={<UsersAdmin />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <UserProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster />
      </UserProvider>
    </div>
  );
}

export default App;
